import ErrorIcon from '@mui/icons-material/Error';
import WarningIcon from '@mui/icons-material/Warning';
import { Box, Chip, DialogActions, Divider, Skeleton, Tooltip } from '@mui/material';
import type { ReactNode } from 'react';

import { ApiWorkflowDialog, Button } from '@verantos-dev/shared-components';
import type { AnalyticalVariable } from '@verantos-dev/types-cohorts';

import type { ConceptGroupOption } from '../../types';
import { AnalyticalVariableCard } from '../AnalyticalVariableCard/AnalyticalVariableCard';

export interface StatusChipConfig {
  label: string;
  backgroundColor: string;
  color: string;
}

/** Name + status chips title row. Used by both AVPreviewModal and AVEditPage. */
export const AVDetailTitle = ({
  name,
  statusChips,
  hasError,
  errorTooltip,
  hasWarning,
  warningTooltip,
}: {
  name: string;
  statusChips?: StatusChipConfig[];
  hasError?: boolean;
  errorTooltip?: string;
  hasWarning?: boolean;
  warningTooltip?: string;
}): ReactNode => (
  <Box
    data-testid="av-detail-title"
    display="flex"
    alignItems="center"
    flexWrap="wrap"
    gap={2.5}
    pr={5}
  >
    <span data-testid="av-detail-title-name">{name}</span>
    {hasError && (
      <Tooltip title={errorTooltip ?? ''} placement="top" arrow>
        <span style={{ display: 'inline-flex', alignItems: 'center' }}>
          <ErrorIcon color="error" sx={{ fontSize: 24 }} />
        </span>
      </Tooltip>
    )}
    {hasWarning && (
      <Tooltip title={warningTooltip ?? ''} placement="top" arrow>
        <span style={{ display: 'inline-flex', alignItems: 'center' }}>
          <WarningIcon color="warning" sx={{ fontSize: 24 }} />
        </span>
      </Tooltip>
    )}
    {statusChips?.map((chip) => (
      <Chip
        key={chip.label}
        data-testid={`av-detail-status-chip-${chip.label.toLowerCase().replace(/\s+/g, '-')}`}
        label={chip.label}
        size="small"
        sx={{
          backgroundColor: chip.backgroundColor,
          color: chip.color,
        }}
      />
    ))}
  </Box>
);

interface AVPreviewModalProps {
  id: string;
  analyticalVariable: AnalyticalVariable;
  conceptGroups: ConceptGroupOption[];
  statusChips?: StatusChipConfig[];
  loading?: boolean;
  onClose: () => void;
  onEditVariable?: () => void;
  actionsDisabled?: boolean;
  actionTooltip?: string;
  owner?: string;
  lastModified?: string;
  hasError?: boolean;
  errorTooltip?: string;
  hasWarning?: boolean;
  warningTooltip?: string;
}

const AVPreviewModalSkeleton = () => (
  <Box
    data-testid="av-preview-modal-skeleton"
    sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}
  >
    {/* Metadata row */}
    <Box sx={{ display: 'flex', gap: 2 }}>
      <Skeleton variant="text" width={140} />
      <Skeleton variant="text" width={120} />
      <Skeleton variant="text" width={160} />
    </Box>
    {/* Description section */}
    <Box>
      <Skeleton variant="text" width={100} sx={{ mb: 1 }} />
      <Skeleton variant="text" width="100%" />
      <Skeleton variant="text" width="80%" />
    </Box>
    {/* Format section */}
    <Box>
      <Skeleton variant="text" width={60} sx={{ mb: 1 }} />
      <Skeleton variant="text" width={100} />
    </Box>
    {/* Logic section */}
    <Skeleton variant="rectangular" height={1} />
    <Box>
      <Skeleton variant="text" width={200} sx={{ mb: 1 }} />
      <Skeleton variant="rectangular" height={120} sx={{ borderRadius: 2 }} />
    </Box>
  </Box>
);

export const AVPreviewModal = ({
  id,
  analyticalVariable,
  conceptGroups,
  statusChips,
  loading,
  onClose,
  onEditVariable,
  actionsDisabled,
  actionTooltip,
  owner,
  lastModified,
  hasError,
  errorTooltip,
  hasWarning,
  warningTooltip,
}: AVPreviewModalProps) => {
  const showEditButton = !!onEditVariable || actionsDisabled;
  return (
    <ApiWorkflowDialog
      id={id}
      title={
        <AVDetailTitle
          name={analyticalVariable.name}
          statusChips={statusChips}
          hasError={hasError}
          errorTooltip={errorTooltip}
          hasWarning={hasWarning}
          warningTooltip={warningTooltip}
        />
      }
      size="large"
      displayButtons={false}
      onClose={onClose}
      isValidForSubmit={false}
      dividers={false}
      dialogContentStyleProps={{ paddingTop: 0 }}
      NonScrollBottomComponent={
        <>
          <Divider />
          <DialogActions data-testid="av-preview-modal-actions" sx={{ gap: 2 }}>
            <Button
              id={`${id}-close-button`}
              btnType={showEditButton ? 'text' : 'primary'}
              displayLabel="Close"
              onClickHandler={onClose}
            />
            {showEditButton ? (
              <Tooltip title={actionsDisabled ? actionTooltip ?? '' : ''}>
                <span>
                  <Button
                    id={`${id}-edit-variable-button`}
                    btnType="primary"
                    displayLabel="Edit variable"
                    onClickHandler={onEditVariable}
                    disabled={actionsDisabled}
                  />
                </span>
              </Tooltip>
            ) : null}
          </DialogActions>
        </>
      }
    >
      {loading ? (
        <AVPreviewModalSkeleton />
      ) : (
        <AnalyticalVariableCard
          analyticalVariable={analyticalVariable}
          conceptGroups={conceptGroups}
          owner={owner}
          lastModified={lastModified}
        />
      )}
    </ApiWorkflowDialog>
  );
};
